import { useEffect, useState } from "react";
import { createPortal } from "react-dom";

import type { MultiSelectOption } from "../../MultiSelect";
import type { AggregateStatus } from "../types";

import { Button as ButtonV2 } from "../../Button";
import { useBodyScrollLock } from "../../hooks/useBodyScrollLock";
import { MultiSelect } from "../../MultiSelect";
import { Typo } from "../../Typo";
import { useSubIBReportContext } from "../providers/SubIBReportProvider";

type SubIBsFilterModalProps = {
  open: boolean;
  onClose: () => void;
};

export const SubIBsFilterModal = ({
  open,
  onClose,
}: SubIBsFilterModalProps) => {
  const { state, actions, options } = useSubIBReportContext();
  useBodyScrollLock(open);

  const [pendingStatuses, setPendingStatuses] = useState<MultiSelectOption[]>(
    [],
  );
  const [pendingPrograms, setPendingPrograms] = useState<MultiSelectOption[]>(
    [],
  );

  const syncInputs = [
    open,
    options.programs,
    options.statuses,
    state.selected.programs,
    state.selected.statuses,
  ] as const;
  const [prevSyncInputs, setPrevSyncInputs] = useState<
    typeof syncInputs | undefined
  >(undefined);

  if (
    !prevSyncInputs ||
    syncInputs.some((input, i) => input !== prevSyncInputs[i])
  ) {
    setPrevSyncInputs(syncInputs);
    if (open) {
      const statusVals = state.selected.statuses ?? [];
      setPendingStatuses(
        options.statuses
          .filter((o) => statusVals.includes(o.value as AggregateStatus))
          .map((o) => ({ value: o.value, label: o.label })),
      );

      const programVals = state.selected.programs ?? [];
      setPendingPrograms(
        options.programs
          .filter((o) => programVals.includes(o.value))
          .map((o) => ({ value: o.value, label: o.label })),
      );
    }
  }

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, onClose]);

  const handleApply = () => {
    actions.setSelected(
      "statuses",
      pendingStatuses.length > 0
        ? (pendingStatuses.map((o) => o.value) as AggregateStatus[])
        : null,
    );
    actions.setSelected(
      "programs",
      pendingPrograms.length > 0 ? pendingPrograms.map((o) => o.value) : null,
    );
    onClose();
  };

  const handleClear = () => {
    setPendingStatuses([]);
    setPendingPrograms([]);
  };

  if (!open) return null;

  return createPortal(
    <div className="fixed inset-0 z-50">
      <div
        className="absolute inset-0"
        style={{ background: "rgba(3,20,46,0.70)" }}
        onClick={onClose}
        aria-hidden="true"
        data-testid="filter-modal-backdrop"
      />

      <div
        className="absolute left-1/2 top-1/2 flex w-full max-w-lg -translate-x-1/2 -translate-y-1/2 flex-col rounded-2xl bg-white p-6 shadow-xl"
        role="dialog"
        aria-modal="true"
        data-testid="filter-modal-panel"
      >
        <div className="flex items-center justify-between">
          <Typo element="h2" type="headingXL" textColor="text-text-primary">
            Filters
          </Typo>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close filters"
            className="text-text-secondary hover:text-text-primary"
          >
            ✕
          </button>
        </div>

        <div className="mt-6 flex flex-col gap-6">
          <MultiSelect
            options={options.programs}
            selected={pendingPrograms}
            onChange={setPendingPrograms}
            placeholder="All rebate programs"
            label="Rebate program"
            modal
          />

          <MultiSelect
            options={options.statuses.map((o) => ({
              value: o.value,
              label: o.label,
            }))}
            selected={pendingStatuses}
            onChange={setPendingStatuses}
            placeholder="All statuses"
            label="Sub IB status"
            modal
          />
        </div>

        <div className="mt-6 flex justify-end gap-3">
          <ButtonV2
            variant="tonal"
            size="md"
            onClick={handleClear}
            data-testid="filter-reset"
          >
            Clear
          </ButtonV2>
          <ButtonV2
            variant="brand"
            size="md"
            onClick={handleApply}
            data-testid="filter-apply"
          >
            Apply
          </ButtonV2>
        </div>
      </div>
    </div>,
    document.body,
  );
};
